import { z } from 'zod';
import {
  actionSchema,
  actorSchema,
  assertionSchema,
  claimSchema,
  credentialSchema,
  diagnosticSchema,
  evidenceEdgeSchema,
  evidenceMethodSchema,
  evidenceSourceSchema,
  integrityAssessmentSchema,
  isoTimestampSchema,
  signalSchema,
  toolSchema,
  trustAssessmentSchema,
  type Diagnostic,
  type VerificationResult,
} from '@upl/evidence-schema';
import type { ArtifactInput } from '@upl/provenance-core';

export const ADAPTER_CATEGORIES = [
  'STANDARD',
  'METADATA',
  'WATERMARK',
  'PROVIDER',
  'CAPTURE_DEVICE',
  'TRUST',
  'OTHER',
] as const;

export const ADAPTER_AVAILABILITIES = [
  'AVAILABLE',
  'NOT_CONFIGURED',
  'RESTRICTED',
  'UNAVAILABLE',
  'EXPERIMENTAL',
  'DISABLED',
] as const;

export const adapterCategorySchema = z.enum(ADAPTER_CATEGORIES);
export const adapterAvailabilitySchema = z.enum(ADAPTER_AVAILABILITIES);
export const executionTargetSchema = z.enum(['BROWSER', 'SERVER']);
export const adapterExecutionStateSchema = z.enum(['COMPLETED', 'SKIPPED', 'FAILED', 'TIMED_OUT']);

export const externalEvidenceRequirementsSchema = z.object({
  requiresNetwork: z.boolean(),
  dataDestination: z.string().min(1).max(300),
  mediaTransmission: z.enum(['NONE', 'ARTIFACT', 'DERIVED_ONLY']),
  privacyClass: z.enum(['LOCAL_ONLY', 'FIRST_PARTY', 'THIRD_PARTY']),
  credentialRequirement: z.enum(['NONE', 'OPTIONAL', 'REQUIRED']),
}).strict();

export const evidenceAdapterDescriptorSchema = z.object({
  id: z.string().min(2).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  version: z.string().min(1).max(80),
  category: adapterCategorySchema,
  capabilities: z.array(z.string().min(1).max(120)).max(50),
  evidence_methods: z.array(evidenceMethodSchema).min(1).max(20),
  availability: adapterAvailabilitySchema,
  execution: z.array(executionTargetSchema).min(1).max(2),
  media_types: z.array(z.string().min(1).max(120)).max(50),
  network: externalEvidenceRequirementsSchema,
}).strict();

export const patchSourceSchema = z.object({
  adapter_id: z.string().min(2).max(80),
  adapter_version: z.string().min(1).max(80),
  retrieved_at: isoTimestampSchema,
  method: z.string().min(1).max(160),
}).strict();

const sourcedDiagnosticSchema = diagnosticSchema.extend({ source: evidenceSourceSchema });
const sourcedEdgeSchema = evidenceEdgeSchema.extend({ source: evidenceSourceSchema });

export const trustEvidenceSchema = z.object({
  id: z.string().min(3).max(160),
  source: evidenceSourceSchema,
  evidence_refs: z.array(z.string().min(3).max(160)).max(100),
  assessment: trustAssessmentSchema,
}).strict();

export const integrityEvidenceSchema = z.object({
  id: z.string().min(3).max(160),
  source: evidenceSourceSchema,
  evidence_refs: z.array(z.string().min(3).max(160)).max(100),
  assessment: integrityAssessmentSchema,
}).strict();

export const evidencePatchSchema = z.object({
  source: patchSourceSchema,
  signals: z.array(signalSchema).max(100),
  credentials: z.array(credentialSchema).max(25),
  claims: z.array(claimSchema).max(50),
  assertions: z.array(assertionSchema).max(100),
  actions: z.array(actionSchema).max(100),
  actors: z.array(actorSchema).max(50),
  tools: z.array(toolSchema).max(50),
  trustEvidence: z.array(trustEvidenceSchema).max(25),
  integrityEvidence: z.array(integrityEvidenceSchema).max(25),
  edges: z.array(sourcedEdgeSchema).max(500),
  warnings: z.array(sourcedDiagnosticSchema).max(50),
  limitations: z.array(sourcedDiagnosticSchema).max(50),
}).strict().superRefine((patch, context) => {
  const expected = { adapter: patch.source.adapter_id, adapter_version: patch.source.adapter_version };
  const sourced = [
    ...patch.signals,
    ...patch.credentials,
    ...patch.claims,
    ...patch.assertions,
    ...patch.actions,
    ...patch.actors,
    ...patch.tools,
    ...patch.trustEvidence,
    ...patch.integrityEvidence,
    ...patch.edges,
    ...patch.warnings,
    ...patch.limitations,
  ];
  for (const [index, evidence] of sourced.entries()) {
    if (evidence.source.adapter !== expected.adapter || evidence.source.adapter_version !== expected.adapter_version) {
      context.addIssue({
        code: 'custom',
        path: ['source', index],
        message: 'Every evidence object must record the producing adapter ID and version',
      });
    }
  }
});

export const adapterExecutionErrorSchema = z.object({
  code: z.string().min(1).max(120),
  message: z.string().max(500).optional(),
  retryable: z.boolean().default(false),
}).strict();

export const adapterExecutionResultSchema = z.object({
  adapter: evidenceAdapterDescriptorSchema,
  execution: z.object({
    state: adapterExecutionStateSchema,
    selected_at: isoTimestampSchema,
    started_at: isoTimestampSchema,
    completed_at: isoTimestampSchema,
    duration_ms: z.number().nonnegative().max(3_600_000),
    method: z.string().min(1).max(160),
  }).strict(),
  evidence: evidencePatchSchema,
  warnings: z.array(sourcedDiagnosticSchema).max(50),
  limitations: z.array(sourcedDiagnosticSchema).max(50),
  source_metadata: z.record(z.string(), z.unknown()).default({}),
  processing_metadata: z.record(z.string(), z.unknown()).default({}),
  errors: z.array(adapterExecutionErrorSchema).max(25),
}).strict();

export type AdapterCategory = z.infer<typeof adapterCategorySchema>;
export type AdapterAvailability = z.infer<typeof adapterAvailabilitySchema>;
export type ExecutionTarget = z.infer<typeof executionTargetSchema>;
export type AdapterExecutionState = z.infer<typeof adapterExecutionStateSchema>;
export type ExternalEvidenceRequirements = z.infer<typeof externalEvidenceRequirementsSchema>;
export type EvidenceAdapterDescriptor = z.infer<typeof evidenceAdapterDescriptorSchema>;
export type PatchSource = z.infer<typeof patchSourceSchema>;
export type TrustEvidence = z.infer<typeof trustEvidenceSchema>;
export type IntegrityEvidence = z.infer<typeof integrityEvidenceSchema>;
export type EvidencePatch = z.infer<typeof evidencePatchSchema>;
export type AdapterExecutionResult = z.infer<typeof adapterExecutionResultSchema>;

export interface EvidenceAdapterContext {
  artifact: ArtifactInput;
  verificationId: string;
  startedAt: string;
  runtime: ExecutionTarget;
  signal?: AbortSignal;
}

export interface EvidenceAdapter {
  readonly descriptor: EvidenceAdapterDescriptor;
  supports(context: EvidenceAdapterContext): boolean;
  inspect(context: EvidenceAdapterContext): Promise<AdapterExecutionResult>;
}

export interface CanonicalBuildOptions {
  mode: VerificationResult['processing']['mode'];
  privacy: VerificationResult['privacy'];
}

export interface AdapterMetricEvent {
  name:
    | 'adapter_selected'
    | 'adapter_started'
    | 'adapter_completed'
    | 'adapter_failed'
    | 'adapter_timed_out'
    | 'adapter_evidence_count'
    | 'adapter_duration_bucket';
  adapter_id: string;
  category: AdapterCategory;
  runtime: ExecutionTarget;
  state?: AdapterExecutionState;
  count?: number;
  duration_bucket?: 'LT_10MS' | 'LT_100MS' | 'LT_1S' | 'LT_10S' | 'GTE_10S';
}

export type AdapterMetricSink = (event: Readonly<AdapterMetricEvent>) => void;

export function createEmptyEvidencePatch(
  descriptor: EvidenceAdapterDescriptor,
  retrievedAt: string,
  method: string,
): EvidencePatch {
  return {
    source: {
      adapter_id: descriptor.id,
      adapter_version: descriptor.version,
      retrieved_at: retrievedAt,
      method,
    },
    signals: [], credentials: [], claims: [], assertions: [], actions: [], actors: [], tools: [],
    trustEvidence: [], integrityEvidence: [], edges: [], warnings: [], limitations: [],
  };
}

export function createAdapterExecutionResult(input: {
  descriptor: EvidenceAdapterDescriptor;
  state: AdapterExecutionState;
  selectedAt: string;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  method: string;
  evidence?: EvidencePatch;
  warnings?: Diagnostic[];
  limitations?: Diagnostic[];
  sourceMetadata?: Record<string, unknown>;
  processingMetadata?: Record<string, unknown>;
  errors?: Array<{ code: string; message?: string; retryable?: boolean }>;
}): AdapterExecutionResult {
  const source = {
    adapter: input.descriptor.id,
    adapter_version: input.descriptor.version,
    provider: input.descriptor.id,
  };
  const stamp = (diagnostic: Diagnostic) => ({ ...diagnostic, source: diagnostic.source ?? source });
  return adapterExecutionResultSchema.parse({
    adapter: input.descriptor,
    execution: {
      state: input.state,
      selected_at: input.selectedAt,
      started_at: input.startedAt,
      completed_at: input.completedAt,
      duration_ms: input.durationMs,
      method: input.method,
    },
    evidence: input.evidence ?? createEmptyEvidencePatch(input.descriptor, input.completedAt, input.method),
    warnings: (input.warnings ?? []).map(stamp),
    limitations: (input.limitations ?? []).map(stamp),
    source_metadata: input.sourceMetadata ?? {},
    processing_metadata: input.processingMetadata ?? {},
    errors: (input.errors ?? []).map((error) => ({ ...error, retryable: error.retryable ?? false })),
  });
}

export function verificationResultToEvidencePatch(
  result: VerificationResult,
  descriptor: EvidenceAdapterDescriptor,
  retrievedAt: string,
  method: string,
): EvidencePatch {
  const source = { adapter: descriptor.id, adapter_version: descriptor.version };
  const stamp = <T extends { source: { adapter: string; provider: string; adapter_version?: string | undefined } }>(value: T): T => ({
    ...value,
    source: { ...value.source, ...source },
  });
  const stampDiagnostic = (value: Diagnostic) => ({
    ...value,
    source: { ...(value.source ?? { provider: descriptor.id }), ...source },
  });
  return evidencePatchSchema.parse({
    source: { adapter_id: descriptor.id, adapter_version: descriptor.version, retrieved_at: retrievedAt, method },
    signals: result.signals.map((signal) => stamp({
      ...signal,
      evidence_method: signal.evidence_method ?? descriptor.evidence_methods[0],
      evidence_strength: signal.evidence_strength ?? (descriptor.id === 'c2pa' ? 'DIRECT_CRYPTOGRAPHIC' : 'UNKNOWN'),
    })),
    credentials: result.credentials.map(stamp),
    claims: result.claims.map(stamp),
    assertions: result.assertions.map(stamp),
    actions: result.actions.map(stamp),
    actors: result.actors.map(stamp),
    tools: result.tools.map(stamp),
    trustEvidence: [{
      id: `trust-evidence:${descriptor.id}`,
      source: { adapter: descriptor.id, adapter_version: descriptor.version, provider: descriptor.id },
      evidence_refs: result.credentials.map(({ id }) => id),
      assessment: result.trust,
    }],
    integrityEvidence: [{
      id: `integrity-evidence:${descriptor.id}`,
      source: { adapter: descriptor.id, adapter_version: descriptor.version, provider: descriptor.id },
      evidence_refs: result.credentials.map(({ id }) => id),
      assessment: result.integrity,
    }],
    edges: result.evidence_graph.edges.map((edge) => ({
      ...edge,
      source: { adapter: descriptor.id, adapter_version: descriptor.version, provider: descriptor.id },
    })),
    warnings: result.warnings.map(stampDiagnostic),
    limitations: result.limitations.map(stampDiagnostic),
  });
}
